import React, {
    forwardRef,
    memo,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";
import * as d3 from "d3";
import {
    Box,
    FormControl,
    IconButton,
    MenuItem,
    Select,
    Tooltip,
    Typography,
} from "@mui/material";
import FormatShapesIcon from "@mui/icons-material/FormatShapes";
import ToolMenu from "../components/toolMenu.js";
import LCFABModal from "../components/lc_fab_modal.js";
import LineToolbar from "../components/LineToolbar.js";
import IvRangeDrawingLayer from "./IvRangeDrawingLayer.jsx";
import {
    getDrawingSurfaceCursor,
    isChartOverlayElement,
} from "../drawing/chartDrawingEngine.js";
import { useTheme } from "@mui/material/styles";


import {
    CONSTANTS,
    WATERMARK_CONFIG,
    KEYBOARD_SHORTCUT,
    OF_KEYBOARD_ZOOM_PAN,
    OUTLINE_HOVER,
    OUTLINE_DEFAULT,
    DRAWING_CONTEXT_MENU_WIDTH,
} from "../../../common/utility/constant";
import { CHART_COLORS } from "../../../common/utility/mp_chart_constants";
import { ToolbarFixed } from "../../../layouts/component.js/index.js";
import {
    AXIS_COLOR,
    AXIS_LABEL_TIME_Y,
    AXIS_TICK_LINE_BOTTOM,
    AXIS_TICK_LINE_TOP,
    AXIS_TICK_STEP_MIN_GAP_PX,
    BOTTOM_PANEL_BACKGROUND,
    BOTTOM_PANEL_BORDER_COLOR,
    BOTTOM_PANEL_GAP,
    BOTTOM_PANEL_HEIGHT_RATIO,
    BOTTOM_PANEL_LINE_KEYS,
    CANDLE_FILL_RATIO,
    CHART_BACKGROUND,
    CHART_MARGIN,
    DAY_BOUNDARY_COLOR,
    DISABLED_LEGEND_COLOR,
    EMPTY_API_PAYLOAD,
    INITIAL_SESSION_VIEW_MAX_K,
    INITIAL_SESSION_VIEW_PADDING,
    IV_CURVE,
    IV_RANGE_BOTTOM_HEIGHT_DEFAULT,
    IV_RANGE_LINE_CONFIG,
    IV_RANGE_LINE_KEYS,
    MAIN_PANEL_LINE_KEYS,
    MAX_CANDLE_WIDTH,
    MIN_BOTTOM_PANEL_HEIGHT,
    MIN_CHART_HEIGHT,
    MIN_CHART_WIDTH,
    MIN_TICK_LABEL_GAP_PX,
    MIN_VISIBLE_CANDLES,
    PAN_DRAG_SPEED,
    PAN_KEY_SPEED,
    RANGE_FILL,
    RIGHT_LABEL_GUTTER,
    SD_BAND_FILL,
    selectSx,
    TICK_LABEL_CHAR_PX,
    TICK_MINUTES,
    TOP_TIME_AXIS_HEIGHT,
    VT_PRIMARY_BLUE,
    ZOOM_OUT_EXTREME,
    WHEEL_SENSITIVITY,
    X_ZOOM_IN_FACTOR,
    X_ZOOM_OUT_FACTOR,
    Y_ZOOM_IN_FACTOR,
    Y_ZOOM_MAX_K,
    Y_ZOOM_MIN_K,
    Y_ZOOM_OUT_FACTOR,
    ZOOM_IN_FACTOR,
    ZOOM_OUT_FACTOR,
} from "./ivRangeConstants";
import {
    cleanApiPayloadSessions,
    cleanRows,
    mergeRowsAtSameTime,
    normalizeSessionRows,
    preparePollSessionRows,
    resolveLiveCloseFromSession,
    resolveLiveMinuteKey,
    rowHasChartField,
    rowHasOhlc,
    rowMinutesFromTime,
    sessionInTradingWindow,
} from "./enrichSessionData";

/*
 * Live data model:
 * apiPayload = first / full response -> chartData (dateList[], cc_data[][])
 * liveData   = each poll -> mergeApiPayload (merge cc_data rows by `time`);
 *              live bar = minute from lastDataTime (or lastTime = mins since midnight);
 *              lastDataTime (with seconds) on right price axis badge only
 *
 * Rendering model (multi-day, two panels):
 * allSessionData = every date's rows, normalized + trading-window-filtered
 *                  PER DAY, then concatenated with a `date` tag on each row.
 * The x-axis is just "slot index into allSessionData" — panning left walks
 * you back through earlier days automatically.
 *
 */
function getLatestDate(payload) {
    if (!payload) return "";
    if (payload.liveDate) return payload.liveDate;
    const list = payload.dateList;
    if (Array.isArray(list) && list.length) return list[list.length - 1];
    return "";
}
function formatPrice(value) {
    // Number(null) is 0 — do not coerce before the finite check.
    if (typeof value !== "number" || !Number.isFinite(value)) return "—";
    return value.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}
/** Live axis badge — same shape as candlestick/orderflow tooltipLiveMarker. */
function formatLiveBadgePrice(value) {
    if (!Number.isFinite(Number(value))) return "—";
    return Number(value).toFixed(2);
}
function formatLiveBadgeTime(lastDataTime) {
    if (!lastDataTime) return "";
    const raw = String(lastDataTime).trim();
    const timePart = raw.includes(" ") ? raw.split(" ").pop() : raw;
    if (timePart.length === 5 && /^\d{1,2}:\d{2}$/.test(timePart)) {
        return `${timePart}:00`;
    }
    return timePart;
}
function formatIv(value) {
    if (typeof value !== "number" || !Number.isFinite(value)) return "—";
    return `${value.toFixed(2)}`;
}
function formatFieldValue(key, row) {
    return key === "iv" || key === "iv1"
        ? formatIv(row[key])
        : formatPrice(row[key]);
}
// Dedupe polls only — not used to pick which bar to draw (see resolveLiveCloseFromSession).
function livePollKey(payload) {
    if (!payload) return null;
    const key = [payload.lastTime, payload.lastDataTime, payload.lastLoadTime]
        .filter((v) => v != null && v !== "")
        .join("|");
    if (key) return key;
    const day = payload.cc_data?.[payload.cc_data.length - 1];
    const bar = Array.isArray(day) ? day[day.length - 1] : null;
    if (!bar) return null;
    return `${bar.time}:${bar.close}:${bar.high}:${bar.low}`;
}
const isNum = (v) => typeof v === "number" && Number.isFinite(v);

function hitTestInfoPanel(root, clientX, clientY, pad = 8) {
    if (!root) return { inPanel: false, control: null };
    const panel = root.getBoundingClientRect();
    const inPanel =
        clientX >= panel.left &&
        clientX <= panel.right &&
        clientY >= panel.top &&
        clientY <= panel.bottom;
    if (!inPanel) return { inPanel: false, control: null };
    const controls = root.querySelectorAll("[data-info-control]");
    for (const el of controls) {
        const r = el.getBoundingClientRect();
        if (
            clientX >= r.left - pad &&
            clientX <= r.right + pad &&
            clientY >= r.top - pad &&
            clientY <= r.bottom + pad
        ) {
            return { inPanel: true, control: el };
        }
    }
    return { inPanel: true, control: null };
}

const LIVE_BOUNDARY_STROKE = "#F7F74F";
// Lines that get a price tag on the right axis (like the live-price badge).
// usd = +SD1 (red), lsd = -SD1 (green).
const AXIS_LABEL_FIELDS = ["usd", "lsd"];

function IvRangeLiveBoundaryMarker({
    layout,
    width,
    xScale,
    liveBoundaryIndex,
    showLiveMarker,
}) {
    if (!showLiveMarker || liveBoundaryIndex < 0) return null;
    const localX = xScale(liveBoundaryIndex + 0.5);
    if (localX < 0 || localX > layout.innerWidth) return null;
    const x = CHART_MARGIN.left + localX;
    const y1 = layout.pricePlotTop;
    const y2 = getVerticalMarkerColumnBottom(layout);
    return (
        <svg
            width={width}
            height={y2}
            style={{
                position: "absolute",
                left: 0,
                top: 0,
                pointerEvents: "none",
                zIndex: 4,
            }}
            aria-hidden
        >
            <line
                x1={x}
                x2={x}
                y1={y1}
                y2={y2}
                stroke={LIVE_BOUNDARY_STROKE}
                strokeWidth={1}
                strokeDasharray="4 3"
            />
        </svg>
    );
}

function useVerticalZoom() {
    const [view, setView] = useState({ k: 1, y: 0 });
    const clamp = useCallback((next) => {
        const k = Math.min(Y_ZOOM_MAX_K, Math.max(Y_ZOOM_MIN_K, next.k));
        return { k, y: next.y };
    }, []);
    const zoom = useCallback(
        (factor, anchorPx) => {
            setView((prev) => {
                const nextK = clamp({ k: prev.k / factor, y: prev.y }).k;
                if (nextK === prev.k) return prev;
                const dataAtCursor = (anchorPx - prev.y) / prev.k;
                const nextY = anchorPx - dataAtCursor * nextK;
                return clamp({ k: nextK, y: nextY });
            });
        },
        [clamp],
    );
    const panBy = useCallback(
        (dyPx) => {
            setView((prev) => clamp({ k: prev.k, y: prev.y + dyPx }));
        },
        [clamp],
    );
    const reset = useCallback(() => setView({ k: 1, y: 0 }), []);
    const transform = useMemo(
        () => d3.zoomIdentity.translate(0, view.y).scale(view.k),
        [view],
    );
    return { transform, zoom, panBy, reset };
}

function computeDefaultXView({
    innerWidth,
    slotCount,
    dataLength,
    lastDayStartIndex,
    dayBarCount,
}) {
    if (!innerWidth || !dataLength) return { k: 1, x: 0 };
    const span = Math.max(1, dataLength);
    const startIdx = Math.max(0, lastDayStartIndex);
    const bars = Math.max(MIN_VISIBLE_CANDLES, dayBarCount || dataLength);
    const kMax = Math.max(1, slotCount / MIN_VISIBLE_CANDLES);
    let k = slotCount / (bars * INITIAL_SESSION_VIEW_PADDING);
    k = Math.min(INITIAL_SESSION_VIEW_MAX_K, kMax, Math.max(1, k));
    let x = -(startIdx * k * innerWidth) / span;
    x = Math.min(0, Math.max(innerWidth * (1 - k), x));
    return { k, x };
}

// x-axis zoom/pan over an abstract "slot count" — doesn't care whether
// those slots are one day or five, which is what makes multi-day panning
// fall out for free.
function useXZoom({
    innerWidth,
    slotCount,
    dataLength,
    defaultXView,
    defaultViewKey,
}) {
    const [view, setView] = useState(
        () => defaultXView ?? { k: 1, x: 0 },
    );
    const baseK = defaultXView?.k ?? 1;
    // Allow zoom until only MIN_VISIBLE_CANDLES span the viewport (candlestick-style).
    const kMax = Math.max(1, slotCount / MIN_VISIBLE_CANDLES);
    const kMin = Math.max(ZOOM_OUT_EXTREME, baseK * ZOOM_OUT_EXTREME);
    const clamp = useCallback(
        (next) => {
            const k = Math.min(kMax, Math.max(kMin, next.k));
            const x = Math.min(0, Math.max(innerWidth * (1 - k), next.x));
            return { k, x };
        },
        [innerWidth, kMax, kMin],
    );
    const panBy = useCallback(
        (px) => {
            setView((prev) => clamp({ k: prev.k, x: prev.x + px }));
        },
        [clamp],
    );
    const zoomBy = useCallback(
        (factor, anchorPx) => {
            setView((prev) => {
                const nextK = Math.min(kMax, Math.max(kMin, prev.k * factor));
                if (nextK === prev.k) return prev;
                const dataAtCursor = (anchorPx - prev.x) / prev.k;
                const nextX = anchorPx - dataAtCursor * nextK;
                return clamp({ k: nextK, x: nextX });
            });
        },
        [clamp, kMax, kMin],
    );
    useEffect(() => {
        if (!defaultXView || !defaultViewKey) return;
        setView(defaultXView);
    }, [defaultViewKey]);

    const reset = useCallback(() => {
        setView(defaultXView ?? { k: 1, x: 0 });
    }, [defaultXView]);
    // Jump a given slot index to the left edge of the viewport, at the
    // current zoom level. Used by the date dropdown to "jump to day X".
    const jumpToIndex = useCallback(
        (index) => {
            setView((prev) => {
                const span = Math.max(1, dataLength || slotCount);
                const basePx = (index / span) * innerWidth;
                const x = -(basePx * prev.k);
                return clamp({ k: prev.k, x });
            });
        },
        [clamp, innerWidth, slotCount, dataLength],
    );
    const { k, x } = clamp(view);
    const xScale = useMemo(() => {
        const base = d3
            .scaleLinear()
            .domain([0, slotCount])
            .range([0, innerWidth]);
        return d3.zoomIdentity.translate(x, 0).scale(k).rescaleX(base);
    }, [slotCount, innerWidth, k, x]);
    const start = dataLength
        ? Math.max(0, Math.floor(xScale.invert(0)) - 1)
        : 0;
    const end = dataLength
        ? Math.min(dataLength, Math.ceil(xScale.invert(innerWidth)) + 1)
        : 0;
    return { xScale, start, end, reset, zoomBy, panBy, jumpToIndex, zoomK: k };
}


function carryBandForward(rows) {
    let lastWithBand = null;
    return rows.map((row) => {
        const hasHigh = isNum(row.high_range);
        const hasLow = isNum(row.low_range);
        if (hasHigh && hasLow) {
            lastWithBand = row;
            return row;
        }
        if (!lastWithBand) return row;
        return {
            ...row,
            high_range: hasHigh ? row.high_range : lastWithBand.high_range,
            low_range: hasLow ? row.low_range : lastWithBand.low_range,
            bandCarriedFrom: lastWithBand.time,
        };
    });
}

// Per-day pipeline: window -> normalize -> carry band. Must run PER DAY
// (not on the concatenated array) or the band and the trading-window edges
// leak across midnight.
function buildDaySession(rawRows) {
    let windowed = sessionInTradingWindow(rawRows);
    if (
        Array.isArray(rawRows) &&
        rawRows.length > 0 &&
        Array.isArray(windowed) &&
        windowed.length < rawRows.length
    ) {
        const last = rawRows[rawRows.length - 1];
        const lastStillIn = windowed.some((r) => r?.time === last?.time);
        // Keep a real bar that sits just outside the window. An all-null
        // pad must not be forced back in — it would extend the x-axis.
        if (!lastStillIn && last?.time && rowHasChartField(last)) {
            windowed = [...windowed, last];
        }
    }
    return padSessionStart(carryBandForward(normalizeSessionRows(windowed)));
}

// Market opens 09:15. The API can return rows starting later (e.g. 11:03 when
// history is missing), and the x-axis is built purely from rows — so without
// this the axis would start at the first row. Prepend empty (all-null) slots
// from market open up to the first real row. Candles/lines simply don't draw
// there (every renderer already skips non-finite fields).
const MARKET_OPEN_MINUTES = 9 * 60 + 15;
function padSessionStart(rows) {
    if (!Array.isArray(rows) || rows.length === 0) return rows;
    const first = rows[0];
    const firstMin = rowMinutesFromTime(first?.time);
    if (!Number.isFinite(firstMin) || firstMin <= MARKET_OPEN_MINUTES) {
        return rows;
    }
    const pads = [];
    const count = Math.min(firstMin - MARKET_OPEN_MINUTES, 600);
    for (let m = MARKET_OPEN_MINUTES; m < MARKET_OPEN_MINUTES + count; m += 1) {
        const hh = String(Math.floor(m / 60)).padStart(2, "0");
        const mm = String(m % 60).padStart(2, "0");
        const pad = {};
        Object.keys(first).forEach((k) => {
            pad[k] = null;
        });
        pad.time = `${hh}:${mm}`;
        pads.push(pad);
    }
    return [...pads, ...rows];
}

// Price panel + SD panel + a small gap between them, sized off the total
// height available. bottomPanelTop/bottomInnerHeight are where the SD
// panel sits; interactiveHeight is how tall the shared drag/zoom/hover
// overlay needs to be to cover both panels.
function getLayout(
    width,
    height,
    bottomPanelHeightRatio = BOTTOM_PANEL_HEIGHT_RATIO,
    showBottomPanel = true,
) {
    const innerWidth = Math.max(
        0,
        width - CHART_MARGIN.left - CHART_MARGIN.right - RIGHT_LABEL_GUTTER,
    );
    const gap = showBottomPanel ? BOTTOM_PANEL_GAP : 0;
    const bottomPanelHeight = showBottomPanel
        ? Math.max(
              MIN_BOTTOM_PANEL_HEIGHT,
              Math.round(height * bottomPanelHeightRatio),
          )
        : 0;
    const pricePanelHeight = Math.max(
        120 + TOP_TIME_AXIS_HEIGHT,
        height - bottomPanelHeight - gap,
    );
    const pricePlotTop = TOP_TIME_AXIS_HEIGHT + CHART_MARGIN.top;
    const priceInnerHeight = Math.max(
        0,
        pricePanelHeight -
            CHART_MARGIN.top -
            CHART_MARGIN.bottom -
            TOP_TIME_AXIS_HEIGHT,
    );
    const bottomPanelTop = pricePanelHeight + gap;
    const bottomInnerHeight = Math.max(
        0,
        bottomPanelHeight - CHART_MARGIN.top - CHART_MARGIN.bottom,
    );
    const pricePlotLocalHeight = pricePanelHeight - pricePlotTop;
    const bottomPlotLocalTop = showBottomPanel
        ? pricePlotLocalHeight + gap
        : null;
    return {
        innerWidth,
        pricePanelHeight,
        bottomPanelHeight,
        bottomPanelTop,
        timeAxisTop: 0,
        timeAxisHeight: TOP_TIME_AXIS_HEIGHT,
        pricePlotTop,
        priceInnerHeight,
        bottomInnerHeight,
        showBottomPanel,
        pricePlotLocalHeight,
        bottomPlotLocalTop,
        interactiveHeight: showBottomPanel
            ? pricePanelHeight - pricePlotTop + gap + bottomPanelHeight
            : pricePanelHeight - pricePlotTop,
    };
}

function getVerticalPanTarget(localY, layout) {
    if (!layout.showBottomPanel) return "price";
    if (localY <= layout.priceInnerHeight) return "price";
    if (
        layout.bottomPlotLocalTop != null &&
        localY >= layout.bottomPlotLocalTop
    ) {
        return "bottom";
    }
    return null;
}

/** Bottom Y for vertical live/crosshair lines — full panel stack, not inner plot only. */
function getVerticalMarkerColumnBottom(layout) {
    if (layout.showBottomPanel) {
        return layout.bottomPanelTop + layout.bottomPanelHeight;
    }
    return layout.pricePanelHeight;
}

// Price scale: candles, range band, close, and any visible SD lines (same
// price units as the underlying).
function buildPriceYScale(data, height, extraFields = []) {
    const fallback = () => d3.scaleLinear().domain([0, 1]).range([height, 0]);
    if (!data.length) return fallback();
    let min = Infinity,
        max = -Infinity;
    for (const d of data) {
        if (Number.isFinite(d.low)) min = Math.min(min, d.low);
        if (Number.isFinite(d.low_range)) min = Math.min(min, d.low_range);
        if (Number.isFinite(d.high)) max = Math.max(max, d.high);
        if (Number.isFinite(d.high_range)) max = Math.max(max, d.high_range);
        if (Number.isFinite(d.close)) {
            min = Math.min(min, d.close);
            max = Math.max(max, d.close);
        }
        for (const f of extraFields) {
            if (Number.isFinite(d[f])) {
                min = Math.min(min, d[f]);
                max = Math.max(max, d[f]);
            }
        }
    }
    if (!Number.isFinite(min) || !Number.isFinite(max)) return fallback();
    const pad = (max - min) * 0.04 || 10;
    return d3
        .scaleLinear()
        .domain([min - pad, max + pad])
        .range([height, 0])
        .nice();
}

function buildIvYScale(data, height, { iv = true, iv1 = true } = {}) {
    const fallback = () => d3.scaleLinear().domain([0, 1]).range([height, 0]);
    if (!data.length) return fallback();
    let min = Infinity,
        max = -Infinity;
    for (const d of data) {
        if (iv && Number.isFinite(d.iv)) {
            min = Math.min(min, d.iv);
            max = Math.max(max, d.iv);
        }
        if (iv1 && Number.isFinite(d.iv1)) {
            min = Math.min(min, d.iv1);
            max = Math.max(max, d.iv1);
        }
    }
    if (!Number.isFinite(min) || !Number.isFinite(max)) return fallback();
    const pad = (max - min) * 0.04 || 1;
    return d3
        .scaleLinear()
        .domain([min - pad, max + pad])
        .range([height, 0])
        .nice();
}

function formatPriceAxisTick(tick, tickStep) {
    if (!Number.isFinite(tick)) return "";
    const step = tickStep ?? 1;
    if (step >= 1) return Math.round(tick).toLocaleString("en-IN");
    if (step >= 0.1) return tick.toFixed(1);
    return tick.toFixed(2);
}

function formatIvAxisTick(tick) {
    if (!Number.isFinite(tick)) return "";
    const abs = Math.abs(tick);
    if (abs >= 100) return Math.round(tick).toLocaleString("en-IN");
    if (abs >= 10) return tick.toFixed(1);
    return tick.toFixed(2);
}

function dedupedAxisTicks(scale, count, formatTick) {
    const seen = new Set();
    const ticks = [];
    for (const tick of scale.ticks(count)) {
        const text = formatTick(tick);
        if (!text || seen.has(text)) continue;
        seen.add(text);
        ticks.push({ tick, text });
    }
    return ticks;
}

function buildRangeAreaPath(data, xScale, yScale, offset) {
    const area = d3
        .area()
        .defined(
            (d) =>
                Number.isFinite(d.low_range) && Number.isFinite(d.high_range),
        )
        .x((d, i) => xScale(offset + i + 0.5))
        .y0((d) => yScale(d.low_range))
        .y1((d) => yScale(d.high_range));
    return area(data) ?? "";
}

//shaded part code
function buildSdBandAreaPath(data, xScale, yScale, offset) {
    const area = d3
        .area()
        .defined((d) => Number.isFinite(d.usd) && Number.isFinite(d.lsd))
        .x((d, i) => xScale(offset + i + 0.5))
        .y0((d) => yScale(d.lsd))
        .y1((d) => yScale(d.usd));
    return area(data) ?? "";
}

// Generic canvas line renderer — used by BOTH the price panel and the SD
// panel. It only knows about "series" (field/scale/color/dash/visible),
// not which panel it's in.
const CanvasLineOverlay = memo(function CanvasLineOverlay({
    width,
    height,
    plotTop,
    innerWidth,
    innerHeight,
    series,
}) {
    const canvasRef = useRef(null);
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || width <= 0 || height <= 0) return;
        const dpr = window.devicePixelRatio || 1;
        canvas.width = Math.max(1, Math.round(width * dpr));
        canvas.height = Math.max(1, Math.round(height * dpr));
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
        const ctx = canvas.getContext("2d");
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, width, height);
        ctx.save();
        ctx.translate(CHART_MARGIN.left, plotTop);
        ctx.beginPath();
        ctx.rect(0, 0, Math.max(0, innerWidth), Math.max(0, innerHeight));
        ctx.clip();
        series.forEach(
            ({
                data,
                field,
                offset,
                xScale,
                yScale,
                color,
                curve,
                lineWidth = 1.5,
                visible = true,
                dash,
            }) => {
                if (!visible || !data || !data.length || !xScale || !yScale)
                    return;
                const line = d3
                    .line()
                    .defined((d) => Number.isFinite(d[field]))
                    .curve(curve || d3.curveLinear)
                    .x((d, i) => xScale(offset + i + 0.5))
                    .y((d) => yScale(d[field]))
                    .context(ctx);
                ctx.beginPath();
                line(data);
                ctx.strokeStyle = color;
                ctx.lineWidth = lineWidth;
                ctx.lineJoin = "round";
                ctx.lineCap = "round";
                ctx.setLineDash(dash || []);
                ctx.globalAlpha = 0.9;
                ctx.stroke();
                ctx.setLineDash([]);
                ctx.globalAlpha = 1;
            },
        );
        ctx.restore();
    }, [width, height, plotTop, innerWidth, innerHeight, series]);
    return (
        <canvas
            ref={canvasRef}
            style={{
                position: "absolute",
                left: 0,
                top: 0,
                width: `${width}px`,
                height: `${height}px`,
                pointerEvents: "none",
            }}
        />
    );
});

function isFullApiPayload(payload) {
    return (
        payload &&
        Array.isArray(payload.dateList) &&
        Array.isArray(payload.cc_data)
    );
}
function isLiveTickPayload(payload) {
    if (
        !payload ||
        !Array.isArray(payload.cc_data) ||
        payload.cc_data.length === 0
    )
        return false;
    return Array.isArray(payload.cc_data[0]);
}
function cloneApiPayload(payload) {
    return {
        ...payload,
        dateList: [...(payload.dateList ?? [])],
        cc_data: (payload.cc_data ?? []).map((session) =>
            Array.isArray(session) ? [...session] : [],
        ),
    };
}

/** Merge a poll into stored session rows — never drop history on small ticks. */
function mergeCandleRows(currentRows = [], incomingRows = [], lastTime) {
    const incoming = preparePollSessionRows(incomingRows, lastTime);
    if (!incoming.length) return preparePollSessionRows(currentRows, lastTime);
    const merged = [...currentRows];
    const indexByTime = new Map();
    merged.forEach((row, index) => {
        if (row?.time) indexByTime.set(row.time, index);
    });
    incoming.forEach((row) => {
        const existingIndex = indexByTime.get(row.time);
        if (existingIndex !== undefined) {
            merged[existingIndex] = mergeRowsAtSameTime(
                merged[existingIndex],
                row,
            );
            return;
        }
        indexByTime.set(row.time, merged.length);
        merged.push(row);
    });
    return preparePollSessionRows(merged, lastTime);
}

/**
 * A poll only carries rows from ~now onward. Whatever branch merges it (full
 * snapshot replace, or merge + prepare/trim), the history already on screen
 * (rows BEFORE the poll's first row) must never be lost — that was the cause
 * of the chart "flashing" the correct 09:15 view and then collapsing to the
 * poll's range.
 */
function preserveEarlierRows(prior = [], next = []) {
    if (!Array.isArray(prior) || prior.length === 0) return next;
    if (!Array.isArray(next) || next.length === 0) return prior;
    const firstNextMin = rowMinutesFromTime(next[0]?.time);
    if (!Number.isFinite(firstNextMin)) return next;
    const earlier = prior.filter((r) => {
        const m = rowMinutesFromTime(r?.time);
        return Number.isFinite(m) && m < firstNextMin;
    });
    return earlier.length ? [...earlier, ...next] : next;
}

function mergeApiPayload(basePayload, incomingPayload) {
    if (
        !isLiveTickPayload(incomingPayload) &&
        !isFullApiPayload(incomingPayload)
    )
        return basePayload;
    const merged = cloneApiPayload(
        isFullApiPayload(basePayload) ? basePayload : incomingPayload,
    );
    const sessions = Array.isArray(incomingPayload.cc_data)
        ? incomingPayload.cc_data
        : [];
    sessions.forEach((session, index) => {
        if (!Array.isArray(session) || session.length === 0) return;
        const date =
            incomingPayload.dateList?.[index] ??
            incomingPayload.liveDate ??
            merged.dateList[merged.dateList.length - 1] ??
            "";
        if (!date) {
            const targetIndex = Math.max(0, merged.cc_data.length - 1);
            if (!merged.cc_data[targetIndex]) merged.cc_data[targetIndex] = [];
            const lastTimeCutoff = incomingPayload.liveDate
                ? incomingPayload.lastTime
                : undefined;
            merged.cc_data[targetIndex] = preserveEarlierRows(
                merged.cc_data[targetIndex],
                mergeCandleRows(
                    merged.cc_data[targetIndex],
                    session,
                    lastTimeCutoff,
                ),
            );
            return;
        }
        let targetIndex = merged.dateList.indexOf(date);
        if (targetIndex === -1) {
            targetIndex = merged.dateList.length;
            merged.dateList.push(date);
            merged.cc_data[targetIndex] = [];
        }
        const sessionDate = merged.dateList[targetIndex];
        const lastTimeCutoff =
            incomingPayload.liveDate &&
            sessionDate === incomingPayload.liveDate
                ? incomingPayload.lastTime
                : undefined;
        const prior = merged.cc_data[targetIndex] ?? [];
        const incomingClean = preparePollSessionRows(session, lastTimeCutoff);
        // Full-day snapshot from the API — replace so projections stay in sync.
        // Row count alone is NOT enough: a poll (now -> projection end) can be
        // larger than a history-only prior and used to wipe 09:15..now.
        // It is a snapshot only if it also starts at/before the prior's first row.
        const coversPriorStart =
            prior.length === 0 ||
            rowMinutesFromTime(incomingClean[0]?.time) <=
                rowMinutesFromTime(prior[0]?.time);
        const isFullSnapshot =
            coversPriorStart &&
            incomingClean.length >= Math.max(60, prior.length * 0.85);
        merged.cc_data[targetIndex] = preserveEarlierRows(
            prior,
            isFullSnapshot
                ? incomingClean
                : mergeCandleRows(prior, session, lastTimeCutoff),
        );
    });
    return cleanApiPayloadSessions({
        ...basePayload,
        ...incomingPayload,
        dateList: merged.dateList,
        cc_data: merged.cc_data,
    });
}

// Tick generation across a CONCATENATED, multi-day array. Resets its
// "day open" reference every time `row.date` changes.
function tickLabelAnchor(x, innerWidth) {
    if (x < 40) return "start";
    if (x > innerWidth - 40) return "end";
    return "middle";
}
function formatAxisDateLabel(date) {
    if (!date) return "";
    return date;
}
// Candlestick x-axis: "DD-MM-YYYY HH:mm" is shown as "DD-MM HH:mm".
function formatCandleStyleAxisLabel(date, time) {
    if (!date && !time) return "";
    if (!date) return time || "";
    if (!time) return String(date);
    const combined = `${date} ${time}`;
    if (/^\d{2}-\d{2}-\d{4} /.test(combined)) {
        return combined.slice(0, 5) + combined.slice(10);
    }
    return combined;
}
function estimateTickLabelWidth(tick, x, innerWidth) {
    const anchor = tickLabelAnchor(x, innerWidth);
    const timeLabel = tick.label ?? "";
    const dateLabel = tick.date ? formatAxisDateLabel(tick.date) : "";
    const w =
        Math.max(dateLabel.length, timeLabel.length) * TICK_LABEL_CHAR_PX;
    return { width: w, anchor };
}
function tickLabelExtents(x, width, anchor) {
    if (anchor === "start") return { left: x, right: x + width };
    if (anchor === "end") return { left: x - width, right: x };
    return { left: x - width / 2, right: x + width / 2 };
}

function buildMultiDayTimeTicks(
    data,
    xScale,
    start,
    end,
    innerWidth,
    minGapPx = AXIS_TICK_STEP_MIN_GAP_PX,
) {
    if (!data.length || end <= start) return [];

    const pxPerSlot = Math.abs(xScale(1) - xScale(0));
    let barMinutes = 1;
    for (let i = 1; i < data.length; i += 1) {
        if (data[i].date === data[i - 1].date) {
            const d = Math.abs(
                rowMinutesFromTime(data[i].time) -
                    rowMinutesFromTime(data[i - 1].time),
            );
            if (d > 0) {
                barMinutes = d;
                break;
            }
        }
    }
    const stepMinutes =
        TICK_MINUTES.find((m) => pxPerSlot * (m / barMinutes) >= minGapPx) ??
        TICK_MINUTES[TICK_MINUTES.length - 1];

    const ticks = [];
    let lastLabelRight = -Infinity;
    let lastDate = null;
    let dayOpenMinutes = 0;
    let lastBucket = null;

    const tryPush = (i, isNewDay) => {
        const row = data[i];
        const x = xScale(i + 0.5);
        if (x < 0 || x > innerWidth) return false;
        const tick = {
            index: i,
            label: row.time,
            date: row.date,
            isDayStart: isNewDay,
        };
        const { width, anchor } = estimateTickLabelWidth(tick, x, innerWidth);
        const extents = tickLabelExtents(x, width, anchor);
        if (extents.left < lastLabelRight + MIN_TICK_LABEL_GAP_PX) return false;
        ticks.push(tick);
        lastLabelRight = extents.right;
        return true;
    };

    for (let i = start; i < end; i += 1) {
        const row = data[i];
        if (!row) continue;

        const isNewDay = row.date !== lastDate;
        const t = rowMinutesFromTime(row.time);
        if (isNewDay) {
            dayOpenMinutes = t;
            lastDate = row.date;
            lastBucket = null;
        }

        const bucket = Math.floor((t - dayOpenMinutes) / stepMinutes);
        if (!isNewDay && bucket === lastBucket) continue;
        if (tryPush(i, isNewDay)) lastBucket = bucket;
    }

    return ticks;
}


function IvRangeToolbar({
    chartStateData,
    instrument,
    dateList,
    selectedDate,
    onDateChange,
    isDrawingToolAllowed,
    isDrawingEnabled,
    selectedTool,
    isMenuOpen,
    drawingMenuPlacement,
    onToggleDrawingMenu,
    onSelectDrawingTool,
    menuRef,
}) {
    const theme = useTheme();
    return (
        <ToolbarFixed
            sx={{
                borderTop: `2px solid ${theme.palette.primaryTheme.shade03}`,
                borderRight: `2px solid ${theme.palette.primaryTheme.shade03}`,
                paddingLeft: 0,
                paddingRight: 0,
                left: chartStateData.left,
                width: chartStateData.width,
                minHeight: 32,
                height: 32,
                overflow: "visible",
            }}
        >
            <Box
                sx={{
                    width: "100%",
                    height: 32,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 1,
                    gap: 2,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        flexShrink: 0,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: "#fff",
                        }}
                    >
                        {instrument}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "#b8c4dc" }}>
                        IV Range
                    </Typography>
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        flexShrink: 0,
                    }}
                >
                    {dateList.length > 0 ? (
                        <FormControl size="small" sx={{ flexShrink: 0 }}>
                            <Select
                                value={selectedDate || ""}
                                onChange={(e) => onDateChange(e.target.value)}
                                sx={selectSx}
                            >
                                {dateList.map((date) => (
                                    <MenuItem
                                        key={date}
                                        value={date}
                                        sx={{ fontSize: 11 }}
                                    >
                                        {date}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : null}
                    {isDrawingToolAllowed ? (
                        <Box
                            ref={menuRef}
                            sx={{
                                position: "relative",
                                display: "inline-flex",
                                overflow: "visible",
                            }}
                        >
                            <Tooltip
                                title="Drawing Tools (double-click in chart as shortcut)"
                                placement="top"
                            >
                                <IconButton
                                    size="small"
                                    onClick={onToggleDrawingMenu}
                                >
                                    <FormatShapesIcon
                                        sx={{
                                            color: isDrawingEnabled
                                                ? "#F7F74F"
                                                : "#fff",
                                            fontSize: 22,
                                        }}
                                    />
                                </IconButton>
                            </Tooltip>
                            {isMenuOpen && drawingMenuPlacement === "toolbar" ? (
                                <ToolMenu
                                    isOpen={isMenuOpen}
                                    selectedTool={selectedTool}
                                    onSelectTool={onSelectDrawingTool}
                                    top={32}
                                    left={-100}
                                />
                            ) : null}
                        </Box>
                    ) : null}
                </Box>
            </Box>
        </ToolbarFixed>
    );
}

// ---------------------------------------------------------------------
// Price panel: candles, range band, and SD lines on one price scale.
// ---------------------------------------------------------------------
const IvRangePriceChart = memo(function IvRangePriceChart({
    id,
    width,
    height,
    innerWidth,
    innerHeight,
    plotTop,
    visibleData,
    offset,
    xScale,
    yScale,
    lineVisibility,
    onToggleLine,
    xTicks,
    dayBoundaries,
    showLiveMarker,
    liveClose,
    lastDataTime,
    liveBoundaryIndex,
    axisLineLabels,
}) {
    const clipId = `${id}-price-clip`;
    const slot = xScale(1) - xScale(0);
    const bodyWidth = Math.min(
        MAX_CANDLE_WIDTH,
        Math.max(1, slot * CANDLE_FILL_RATIO),
    );
    const rangePath = useMemo(
        () => buildRangeAreaPath(visibleData, xScale, yScale, offset),
        [visibleData, xScale, yScale, offset],
    );
    const yAxisTicks = useMemo(() => {
        const raw = yScale.ticks(7);
        const tickStep =
            raw.length >= 2 ? Math.abs(raw[1] - raw[0]) : 1;
        return dedupedAxisTicks(yScale, 7, (tick) =>
            formatPriceAxisTick(tick, tickStep),
        );
    }, [yScale]);
    const LIVE_TIME_BADGE_OFFSET_Y = 16; // push below the tick label row
    const liveTimeBadgeCenterY = AXIS_LABEL_TIME_Y + LIVE_TIME_BADGE_OFFSET_Y;
    const priceLineSeries = useMemo(
        () =>
            MAIN_PANEL_LINE_KEYS.map((key) => {
                const config = IV_RANGE_LINE_CONFIG[key];
                return {
                    data: visibleData,
                    field: config.field,
                    offset,
                    xScale,
                    yScale,
                    color: config.color,
                    curve: d3.curveMonotoneX,
                    lineWidth: 1.5,
                    visible: lineVisibility[key],
                    dash: config.dash,
                };
            }),
        [visibleData, offset, xScale, yScale, lineVisibility],
    );
    const sdBandPath = useMemo(
        () =>
            lineVisibility.usd && lineVisibility.lsd
                ? buildSdBandAreaPath(visibleData, xScale, yScale, offset)
                : "",
        [
            visibleData,
            xScale,
            yScale,
            offset,
            lineVisibility.usd,
            lineVisibility.lsd,
        ],
    );
    const legendPositions = useMemo(() => {
        let x = 0;
        const positions = {};
        MAIN_PANEL_LINE_KEYS.forEach((key) => {
            positions[key] = x;
            x += IV_RANGE_LINE_CONFIG[key].label.length * 7 + 24;
        });
        return positions;
    }, []);

    const liveY = useMemo(() => {
        if (!showLiveMarker || !Number.isFinite(Number(liveClose))) return null;
        return yScale(liveClose);
    }, [showLiveMarker, liveClose, yScale]);

    const showLiveOverlay =
        showLiveMarker &&
        liveY != null &&
        liveY >= 0 &&
        liveY <= innerHeight &&
        lastDataTime;

    const liveBadgeWidth = Math.max(
        0,
        RIGHT_LABEL_GUTTER - CHART_MARGIN.right - 4,
    );
    const livePriceBadgeHeight = 22;
    const livePriceTimeBadgeHeight = 38;
    const liveBadgeX = innerWidth + 2;
    const liveTimeLabel = formatLiveBadgeTime(lastDataTime);
    const liveTimeAxisX =
        liveBoundaryIndex >= 0 ? xScale(liveBoundaryIndex + 0.5) : null;
    const showLiveTimeAxisBadge = Boolean(
        showLiveMarker && liveTimeLabel && liveTimeAxisX != null,
    );
    const liveTimeBadgeWidth = Math.max(52, liveTimeLabel.length * 7 + 10);

    return (
        <>
            <svg width={width} height={height} className="iv-range-price-chart">
                <defs>
                    <clipPath id={clipId}>
                        <rect
                            x={0}
                            y={0}
                            width={innerWidth}
                            height={innerHeight}
                        />
                    </clipPath>
                </defs>
                <rect width={width} height={height} fill={CHART_BACKGROUND} />
                <g
                    transform={`translate(${CHART_MARGIN.left},${CHART_MARGIN.top})`}
                >
                    {xTicks.map((tick) => {
                        const x = xScale(tick.index + 0.5);
                        if (x < -8 || x > innerWidth + 8) return null;
                        const anchor = tickLabelAnchor(x, innerWidth);
                        return (
                            <g key={`top-time-${tick.index}`}>
                                <line
                                    x1={x}
                                    x2={x}
                                    y1={AXIS_TICK_LINE_TOP}
                                    y2={AXIS_TICK_LINE_BOTTOM}
                                    stroke={AXIS_COLOR}
                                />
                                <text
                                    x={x}
                                    y={AXIS_LABEL_TIME_Y}
                                    textAnchor={anchor}
                                    fill="#ffffff"
                                    fontSize={12}
                                    fontFamily="sans-serif"
                                    fontWeight={500}
                                >
                                    {formatCandleStyleAxisLabel(
                                        tick.date,
                                        tick.label,
                                    )}
                                </text>
                            </g>
                        );
                    })}
                    {showLiveTimeAxisBadge &&
                        liveTimeAxisX >= -8 &&
                        liveTimeAxisX <= innerWidth + 8 && (
                            <g key="live-time-axis-badge">
                                <rect
                                    x={liveTimeAxisX - liveTimeBadgeWidth / 2}
                                    y={
                                        liveTimeBadgeCenterY -
                                        livePriceBadgeHeight / 2
                                    }
                                    width={liveTimeBadgeWidth}
                                    height={livePriceBadgeHeight}
                                    fill={CHART_COLORS.LIVE_PRICE_CONTAINER_BG}
                                    stroke={CHART_COLORS.LIVE_PRICE_LINE}
                                    strokeWidth={1}
                                    rx={2}
                                />
                                <text
                                    x={liveTimeAxisX}
                                    y={liveTimeBadgeCenterY}
                                    textAnchor="middle"
                                    dominantBaseline="middle"
                                    fill={CHART_COLORS.MODAL_TEXT_ENABLED}
                                    fontSize={12}
                                    fontWeight={600}
                                >
                                    {liveTimeLabel}
                                </text>
                            </g>
                        )}
                </g>
                <g transform={`translate(${CHART_MARGIN.left},${plotTop})`}>
                    {yAxisTicks.map(({ tick, text }) => (
                        <text
                            key={tick}
                            x={innerWidth + 8}
                            y={yScale(tick)}
                            textAnchor="start"
                            dominantBaseline="middle"
                            fill="#ffffff"
                            fontSize={12}
                            fontWeight={600}
                        >
                            {text}
                        </text>
                    ))}
                    {axisLineLabels?.map(({ key, value, color, time }) => {
                        const y = yScale(value);
                        if (!Number.isFinite(y) || y < 0 || y > innerHeight)
                            return null;
                        return (
                            <g
                                key={`axis-line-label-${key}`}
                                transform={`translate(${innerWidth + 2}, ${y})`}
                            >
                                <rect
                                    x={0}
                                    y={-livePriceTimeBadgeHeight / 2}
                                    width={liveBadgeWidth}
                                    height={livePriceTimeBadgeHeight / 1.5}
                                    fill={CHART_BACKGROUND}
                                    stroke={color}
                                    strokeWidth={1}
                                    rx={2}
                                />
                                <text
                                    x={liveBadgeWidth / 2}
                                    y={-6}
                                    textAnchor="middle"
                                    dominantBaseline="middle"
                                    fill={color}
                                    fontSize={11}
                                    fontWeight={600}
                                >
                                    {formatLiveBadgePrice(value)}
                                </text>
                                {/* <text
                                    x={liveBadgeWidth / 2}
                                    y={10}
                                    textAnchor="middle"
                                    dominantBaseline="middle"
                                    fill={color}
                                    fontSize={11}
                                    fontWeight={500}
                                >
                                    {time || "—"}
                                </text> */}
                            </g>
                        );
                    })}
                    <g clipPath={`url(#${clipId})`}>
                        {rangePath && (
                            <path
                                d={rangePath}
                                fill={RANGE_FILL}
                                stroke="none"
                            />
                        )}
                        {sdBandPath && (
                            <path
                                d={sdBandPath}
                                fill={SD_BAND_FILL}
                                stroke="none"
                            />
                        )}
                        {dayBoundaries.map((b) => {
                            const x = xScale(b.index);
                            if (x < -4 || x > innerWidth + 4) return null;
                            return (
                                <line
                                    key={`day-${b.index}`}
                                    x1={x}
                                    x2={x}
                                    y1={0}
                                    y2={innerHeight}
                                    stroke={DAY_BOUNDARY_COLOR}
                                    strokeWidth={1}
                                    strokeDasharray="2 3"
                                />
                            );
                        })}
                        {visibleData.map((d, j) => {
                            const hasOhlc =
                                Number.isFinite(d.open) &&
                                Number.isFinite(d.high) &&
                                Number.isFinite(d.low) &&
                                Number.isFinite(d.close);
                            if (!hasOhlc) return null;
                            const i = offset + j;
                            const cx = xScale(i + 0.5);
                            const isUp = d.close >= d.open;
                            const color = isUp
                                ? CHART_COLORS.CANDLE_GREEN
                                : CHART_COLORS.CANDLE_RED;
                            const yOpen = yScale(d.open);
                            const yClose = yScale(d.close);
                            const bodyTop = Math.min(yOpen, yClose);
                            const bodyHeight = Math.max(
                                1,
                                Math.abs(yClose - yOpen),
                            );
                            return (
                                <g key={`${d.date}-${d.time}-${i}`}>
                                    <line
                                        x1={cx}
                                        x2={cx}
                                        y1={yScale(d.high)}
                                        y2={yScale(d.low)}
                                        stroke={color}
                                        strokeWidth={1}
                                    />
                                    <rect
                                        x={cx - bodyWidth / 2}
                                        y={bodyTop}
                                        width={bodyWidth}
                                        height={bodyHeight}
                                        fill={color}
                                    />
                                </g>
                            );
                        })}
                    </g>
                    {showLiveOverlay ? (
                        <>
                            {/* this is dashed line showing the live ticks */}
                            <line
                                x1={0}
                                x2={innerWidth}
                                y1={liveY}
                                y2={liveY}
                                stroke="#ffffff"
                                strokeWidth={1}
                                strokeDasharray="4 4"
                            />
                            <g transform={`translate(${liveBadgeX}, ${liveY})`}>
                                <rect
                                    x={0}
                                    y={-livePriceTimeBadgeHeight / 2}
                                    width={liveBadgeWidth}
                                    height={livePriceTimeBadgeHeight}
                                    fill={CHART_COLORS.LIVE_PRICE_CONTAINER_BG}
                                    stroke={CHART_COLORS.LIVE_PRICE_LINE}
                                    strokeWidth={1}
                                    rx={2}
                                />
                                <text
                                    x={liveBadgeWidth / 2}
                                    y={-6}
                                    textAnchor="middle"
                                    dominantBaseline="middle"
                                    fill={CHART_COLORS.MODAL_TEXT_ENABLED}
                                    fontSize={12}
                                    fontWeight={600}
                                >
                                    {formatLiveBadgePrice(liveClose)}
                                </text>
                                <text
                                    x={liveBadgeWidth / 2}
                                    y={10}
                                    textAnchor="middle"
                                    dominantBaseline="middle"
                                    fill={CHART_COLORS.MODAL_TEXT_ENABLED}
                                    fontSize={11}
                                    fontWeight={500}
                                >
                                    {formatLiveBadgeTime(lastDataTime)}
                                </text>
                            </g>
                        </>
                    ) : null}
                </g>
                <g
                    transform={`translate(${CHART_MARGIN.left + 6},${
                        plotTop + 14
                    })`}
                >
                    {MAIN_PANEL_LINE_KEYS.map((key) => {
                        const config = IV_RANGE_LINE_CONFIG[key];
                        const active = lineVisibility[key];
                        return (
                            <text
                                key={key}
                                x={legendPositions[key]}
                                fill={
                                    active
                                        ? config.color
                                        : DISABLED_LEGEND_COLOR
                                }
                                fontSize={11}
                                fontWeight={600}
                                style={{
                                    cursor: onToggleLine
                                        ? "pointer"
                                        : "default",
                                }}
                                onClick={
                                    onToggleLine
                                        ? () => onToggleLine(key)
                                        : undefined
                                }
                            >
                                ● {config.label}
                            </text>
                        );
                    })}
                </g>
            </svg>
            <CanvasLineOverlay
                width={width}
                height={height}
                plotTop={plotTop}
                innerWidth={innerWidth}
                innerHeight={innerHeight}
                series={priceLineSeries}
            />
        </>
    );
});

// ---------------------------------------------------------------------
// SD panel: +SD1 / -SD1 / Next +SD1 / Next -SD1, its own scale, its own
// legend. Self-contained — every input is a prop, nothing reaches into
// outer closures — so this is a straight lift into its own file whenever
// you want it there for the Settings work.
// ---------------------------------------------------------------------
const IvRangeBottomPanel = memo(function IvRangeBottomPanel({
    id,
    width,
    height,
    innerWidth,
    innerHeight,
    visibleData,
    offset,
    xScale,
    ivScale,
    hasIv1,
    lineVisibility,
    onToggleLine,
    dayBoundaries,
}) {
    const clipId = `${id}-sd-clip`;
    const yTicks = useMemo(
        () => dedupedAxisTicks(ivScale, 4, formatIvAxisTick),
        [ivScale],
    );

    const bottomLineSeries = useMemo(
        () =>
            BOTTOM_PANEL_LINE_KEYS.filter(
                (key) => key !== "iv1" || hasIv1,
            ).map((key) => {
                const config = IV_RANGE_LINE_CONFIG[key];
                return {
                    data: visibleData,
                    field: config.field,
                    offset,
                    xScale,
                    yScale: ivScale,
                    color: config.color,
                    curve:
                        config.field === "iv" ? IV_CURVE : d3.curveMonotoneX,
                    lineWidth: 1.5,
                    visible: lineVisibility[key],
                    dash: config.dash,
                };
            }),
        [visibleData, offset, xScale, ivScale, lineVisibility, hasIv1],
    );

    const legendPositions = useMemo(() => {
        let x = 0;
        const positions = {};
        BOTTOM_PANEL_LINE_KEYS.forEach((key) => {
            if (key === "iv1" && !hasIv1) return;
            positions[key] = x;
            x += IV_RANGE_LINE_CONFIG[key].label.length * 7 + 24;
        });
        return positions;
    }, [hasIv1]);

    return (
        <>
            <svg width={width} height={height} className="iv-range-sd-panel">
                <defs>
                    <clipPath id={clipId}>
                        <rect
                            x={0}
                            y={0}
                            width={innerWidth}
                            height={innerHeight}
                        />
                    </clipPath>
                </defs>
                <rect
                    width={width}
                    height={height}
                    fill={BOTTOM_PANEL_BACKGROUND}
                />
                <rect
                    x={0}
                    y={0}
                    width={width}
                    height={1}
                    fill={BOTTOM_PANEL_BORDER_COLOR}
                />
                <g
                    transform={`translate(${CHART_MARGIN.left},${CHART_MARGIN.top})`}
                >
                    {yTicks.map(({ tick, text }) => (
                        <text
                            key={text}
                            x={innerWidth + 8}
                            y={ivScale(tick)}
                            textAnchor="start"
                            dominantBaseline="middle"
                            fill="#ffffff"
                            fontSize={11}
                            fontWeight={600}
                        >
                            {text}
                        </text>
                    ))}
                    <g clipPath={`url(#${clipId})`}>
                        {dayBoundaries.map((b) => {
                            const x = xScale(b.index);
                            if (x < -4 || x > innerWidth + 4) return null;
                            return (
                                <line
                                    key={`sd-day-${b.index}`}
                                    x1={x}
                                    x2={x}
                                    y1={0}
                                    y2={innerHeight}
                                    stroke={DAY_BOUNDARY_COLOR}
                                    strokeWidth={1}
                                    strokeDasharray="2 3"
                                />
                            );
                        })}
                    </g>
                    <g transform="translate(6,14)">
                        {BOTTOM_PANEL_LINE_KEYS.map((key) => {
                            if (key === "iv1" && !hasIv1) return null;
                            const config = IV_RANGE_LINE_CONFIG[key];
                            const active = lineVisibility[key];
                            return (
                                <text
                                    key={key}
                                    x={legendPositions[key]}
                                    fill={
                                        active
                                            ? config.color
                                            : DISABLED_LEGEND_COLOR
                                    }
                                    fontSize={11}
                                    fontWeight={600}
                                    style={{
                                        cursor: onToggleLine
                                            ? "pointer"
                                            : "default",
                                    }}
                                    onClick={
                                        onToggleLine
                                            ? () => onToggleLine(key)
                                            : undefined
                                    }
                                >
                                    ● {config.label}
                                </text>
                            );
                        })}
                    </g>
                </g>
            </svg>
            <CanvasLineOverlay
                width={width}
                height={height}
                plotTop={CHART_MARGIN.top}
                innerWidth={innerWidth}
                innerHeight={innerHeight}
                series={bottomLineSeries}
            />
        </>
    );
});

function InfoItem({ label, value, color = "#fff", active = true, onClick }) {
    const clickable = typeof onClick === "function";
    return (
        <span
            data-info-control={clickable ? "true" : undefined}
            onClick={onClick}
            onPointerDown={
                clickable ? (event) => event.stopPropagation() : undefined
            }
            title={
                clickable
                    ? active
                        ? `Hide ${label}`
                        : `Show ${label}`
                    : undefined
            }
            style={{
                display: "inline-block",
                whiteSpace: "nowrap",
                marginRight: 12,
                opacity: active ? 1 : 0.35,
                cursor: clickable ? "pointer" : "default",
                pointerEvents: clickable ? "auto" : "none",
                userSelect: "none",
            }}
        >
            <span style={{ fontSize: 11, color: "#c8d8ee", marginRight: 4 }}>
                {label}:
            </span>
            <span
                style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: active ? color : "#5b6b8c",
                }}
            >
                {value}
            </span>
        </span>
    );
}

function IvRangeInfoPanel({
    row,
    displayTime,
    top,
    left,
    lineVisibility,
    onToggleLine,
    hasIv1,
    panelRef,
}) {
    const [open, setOpen] = useState(true);
    if (!row) return null;
    const hasOhlc = rowHasOhlc(row);
    const up = hasOhlc && row.close >= row.open;
    const c = hasOhlc
        ? up
            ? CHART_COLORS.CANDLE_GREEN
            : CHART_COLORS.CANDLE_RED
        : "#fff";
    const carried = Boolean(row.bandCarriedFrom);
    return (
        <div
            ref={panelRef}
            style={{
                position: "absolute",
                top,
                left,
                zIndex: 6,
                pointerEvents: "none",
                background: "rgba(2,20,45,0.92)",
                border: "1px solid #163569",
                borderRadius: 4,
                padding: "6px 8px",
                minWidth: 320,
                fontFamily: "sans-serif",
                userSelect: "none",
            }}
        >
            <div style={{ display: "flex", alignItems: "center" }}>
                <InfoItem label="Date" value={row.date} />
                <InfoItem label="Time" value={displayTime ?? row.time} />
                <InfoItem label="O" value={formatPrice(row.open)} color={c} />
                <InfoItem label="H" value={formatPrice(row.high)} color={c} />
                <InfoItem label="L" value={formatPrice(row.low)} color={c} />
                <InfoItem label="C" value={formatPrice(row.close)} color={c} />
                <button
                    type="button"
                    data-info-control="true"
                    onClick={() => setOpen((v) => !v)}
                    style={{
                        pointerEvents: "none",
                        marginLeft: "auto",
                        flexShrink: 0,
                        width: 22,
                        height: 22,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "none",
                        borderRadius: 3,
                        background: VT_PRIMARY_BLUE,
                        color: "#fff",
                        fontSize: 10,
                        lineHeight: 1,
                    }}
                >
                    {open ? "▲" : "▼"}
                </button>
            </div>
            {open && (
                <div
                    style={{ marginTop: 6, display: "flex", flexWrap: "wrap" }}
                >
                    {IV_RANGE_LINE_KEYS.map((key) => {
                        if (key === "iv1" && !hasIv1) return null;
                        const config = IV_RANGE_LINE_CONFIG[key];
                        return (
                            <InfoItem
                                key={key}
                                label={config.label}
                                value={formatFieldValue(key, row)}
                                color={config.color}
                                active={lineVisibility?.[key]}
                                onClick={
                                    onToggleLine
                                        ? () => onToggleLine(key)
                                        : undefined
                                }
                            />
                        );
                    })}
                    {carried && (
                        <span style={{ fontSize: 10, color: "#8b9bb8" }}>
                            band from {row.bandCarriedFrom}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}

function paintIvRangeCrosshair(canvas, layout, width, height, frame) {
    if (!canvas || width <= 0 || height <= 0) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.round(width * dpr));
    canvas.height = Math.max(1, Math.round(height * dpr));
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    if (frame?.enabled !== true || frame.boxY == null) return;

    const left = CHART_MARGIN.left;
    const right = width - CHART_MARGIN.right;
    const boxY = frame.boxY;
    const columnBottom = getVerticalMarkerColumnBottom(layout);
    const verticalX = frame.drawX ?? frame.pointerX;

    ctx.save();
    ctx.strokeStyle = "rgba(255,255,255,0.7)";
    ctx.setLineDash([2, 2]);
    ctx.lineWidth = 1;
    if (verticalX != null) {
        ctx.beginPath();
        ctx.moveTo(verticalX, 0);
        ctx.lineTo(verticalX, columnBottom);
        ctx.stroke();
    }
    ctx.beginPath();
    ctx.moveTo(left, boxY);
    ctx.lineTo(right, boxY);
    ctx.stroke();
    ctx.restore();

    paintCrosshairAxisTag(ctx, frame.timeLabel, {
        x: verticalX,
        y: CHART_MARGIN.top + TOP_TIME_AXIS_HEIGHT / 2,
        align: "center",
        minX: left,
        maxX: left + layout.innerWidth,
    });
    if (frame.priceLabel) {
        const gutterLeft = left + layout.innerWidth + 2;
        paintCrosshairAxisTag(ctx, frame.priceLabel, {
            x: gutterLeft,
            y: boxY,
            align: "left",
            minX: gutterLeft,
            maxX: right,
            minY: layout.pricePlotTop + 10,
            maxY: layout.pricePlotTop + layout.priceInnerHeight - 10,
        });
    }

    if (frame.showDiamond && frame.diamondX != null) {
        const x = frame.diamondX;
        ctx.save();
        ctx.setLineDash([]);
        ctx.fillStyle = "#022D5A";
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(x, boxY - 7);
        ctx.lineTo(x + 7, boxY);
        ctx.lineTo(x, boxY + 7);
        ctx.lineTo(x - 7, boxY);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        ctx.restore();
    }
}

// Same chip as candlestick `.tooltipCrossHair`: price on the right axis,
// date+time centered on the top time axis.
function paintCrosshairAxisTag(ctx, text, box) {
    if (!text || box.x == null || !Number.isFinite(box.x)) return;
    ctx.save();
    ctx.font = "12px sans-serif";
    ctx.setLineDash([]);
    const padX = 4;
    const height = 18;
    const width = ctx.measureText(text).width + padX * 2;
    let x = box.align === "center" ? box.x - width / 2 : box.x;
    let y = box.y - height / 2;
    if (box.minX != null) x = Math.max(box.minX, x);
    if (box.maxX != null) x = Math.min(box.maxX - width, x);
    if (box.minY != null) y = Math.max(box.minY - height / 2, y);
    if (box.maxY != null) y = Math.min(box.maxY - height / 2, y);
    ctx.fillStyle = "#475569";
    ctx.strokeStyle = "gray";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(x, y, width, height, 2);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text, x + width / 2, y + height / 2);
    ctx.restore();
}

const IvRangeCrosshairCanvas = memo(
    forwardRef(function IvRangeCrosshairCanvas(
        { layout, width, height },
        ref,
    ) {
        const canvasRef = useRef(null);
        const layoutRef = useRef(layout);
        const sizeRef = useRef({ width, height });
        const lastFrameRef = useRef(null);

        layoutRef.current = layout;
        sizeRef.current = { width, height };

        const paint = useCallback((frame) => {
            lastFrameRef.current = frame;
            paintIvRangeCrosshair(
                canvasRef.current,
                layoutRef.current,
                sizeRef.current.width,
                sizeRef.current.height,
                frame,
            );
        }, []);

        useImperativeHandle(ref, () => ({ paint }), [paint]);

        useEffect(() => {
            if (lastFrameRef.current) {
                paint(lastFrameRef.current);
            }
        }, [layout, width, height, paint]);

        return (
            <canvas
                ref={canvasRef}
                style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    width: `${width}px`,
                    height: `${height}px`,
                    pointerEvents: "none",
                    zIndex: 5,
                }}
            />
        );
    }),
);

function IvRangeChartBody({
    id,
    width,
    height,
    layout,
    visibleData,
    offset,
    xScale,
    priceScale,
    ivScale,
    hasIv1,
    lineVisibility,
    onToggleLine,
    xTicks,
    dayBoundaries,
    hoverIndex,
    hoverY,
    hoverRow,
    infoRow,
    infoDisplayTime,
    interactionRef,
    handlePointerDown,
    handleChartPanMove,
    handleInteractionPointerMove,
    handlePointerUp,
    handleWheel,
    handleOverlayEnter,
    handleOverlayLeave,
    handleOverlayMove,
    infoPanelRef,
    onHitInfoPanelControl,
    handleKeyDown,
    handleKeyUp,
    onChartDoubleClick,
    sessionData,
    isDrawingEnabled,
    selectedTool,
    drawingObjects,
    setDrawingObjects,
    disableDrawing,
    selectedDrawingObject,
    onSelectedDrawingObjectChange,
    drawingLayerRef,
    instrument,
    updateCrosshairFromPointer,
    crosshairRef,
    onDrawingObjectDragChange,
    showLiveMarker,
    liveClose,
    lastDataTime,
    liveBoundaryIndex,
    axisLineLabels,
}) {
    return (
        <Box
            id={id}
            className={id}
            sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                backgroundColor: CHART_BACKGROUND,
                color: "#d8f6fa",
                overflow: "hidden",
            }}
        >
            <div id={`watermark${id}`} className="watermark">
                <text>{WATERMARK_CONFIG.TEXT}</text>
            </div>

            <Box
                sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: layout.pricePanelHeight,
                }}
            >
                <IvRangePriceChart
                    id={id}
                    width={width}
                    height={layout.pricePanelHeight}
                    innerWidth={layout.innerWidth}
                    innerHeight={layout.priceInnerHeight}
                    plotTop={layout.pricePlotTop}
                    visibleData={visibleData}
                    offset={offset}
                    xScale={xScale}
                    yScale={priceScale}
                    lineVisibility={lineVisibility}
                    onToggleLine={onToggleLine}
                    xTicks={xTicks}
                    dayBoundaries={dayBoundaries}
                    showLiveMarker={showLiveMarker}
                    liveClose={liveClose}
                    lastDataTime={lastDataTime}
                    liveBoundaryIndex={liveBoundaryIndex}
                    axisLineLabels={axisLineLabels}
                />
                <IvRangeDrawingLayer
                    ref={drawingLayerRef}
                    width={layout.innerWidth}
                    height={layout.priceInnerHeight}
                    left={CHART_MARGIN.left}
                    top={layout.pricePlotTop}
                    sessionData={sessionData}
                    xScale={xScale}
                    priceScale={priceScale}
                    isDrawingEnabled={isDrawingEnabled}
                    selectedTool={selectedTool}
                    drawingObjects={drawingObjects}
                    setDrawingObjects={setDrawingObjects}
                    disableDrawing={disableDrawing}
                    onSelectedObjectChange={onSelectedDrawingObjectChange}
                    onForwardPointerDown={handlePointerDown}
                    onForwardPointerMove={handleChartPanMove}
                    onForwardPointerUp={handlePointerUp}
                    onForwardWheel={handleWheel}
                    onDoubleClick={onChartDoubleClick}
                    onForwardHoverMove={updateCrosshairFromPointer}
                    onForwardHoverEnter={handleOverlayEnter}
                    onForwardHoverLeave={handleOverlayLeave}
                    onHitInfoPanelControl={onHitInfoPanelControl}
                    onDrawingObjectDragChange={onDrawingObjectDragChange}
                    instrument={instrument}
                />
            </Box>

            <Box
                sx={{
                    position: "absolute",
                    top: layout.bottomPanelTop,
                    left: 0,
                    width: "100%",
                    height: layout.bottomPanelHeight,
                }}
            >
                <IvRangeBottomPanel
                    id={id}
                    width={width}
                    height={layout.bottomPanelHeight}
                    innerWidth={layout.innerWidth}
                    innerHeight={layout.bottomInnerHeight}
                    visibleData={visibleData}
                    offset={offset}
                    xScale={xScale}
                    ivScale={ivScale}
                    hasIv1={hasIv1}
                    lineVisibility={lineVisibility}
                    onToggleLine={onToggleLine}
                    dayBoundaries={dayBoundaries}
                />
            </Box>

            <div
                ref={interactionRef}
                tabIndex={0}
                data-chart-overlay="true"
                style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    width: "100%",
                    height: layout.pricePlotTop + layout.interactiveHeight,
                    zIndex: 2,
                    cursor: getDrawingSurfaceCursor(isDrawingEnabled),
                    touchAction: "none",
                    outline: OUTLINE_DEFAULT,
                    outlineOffset: "-1px",
                }}
                onPointerDown={handlePointerDown}
                onPointerMove={handleInteractionPointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
                // onWheel={handleWheel} // have to remove this
                onMouseEnter={handleOverlayEnter}
                onMouseLeave={handleOverlayLeave}
                onKeyDown={handleKeyDown}
                onKeyUp={handleKeyUp}
                onDoubleClick={onChartDoubleClick}
            />

            <IvRangeLiveBoundaryMarker
                layout={layout}
                width={width}
                xScale={xScale}
                liveBoundaryIndex={liveBoundaryIndex}
                showLiveMarker={showLiveMarker}
            />

            <IvRangeCrosshairCanvas
                ref={crosshairRef}
                layout={layout}
                width={width}
                height={height}
            />

            <IvRangeInfoPanel
                panelRef={infoPanelRef}
                row={infoRow}
                displayTime={infoDisplayTime}
                top={layout.pricePlotTop + 6}
                left={CHART_MARGIN.left + 6}
                lineVisibility={lineVisibility}
                onToggleLine={onToggleLine}
                hasIv1={hasIv1}
            />
        </Box>
    );
}
const IvRangeChart = ({
    chartStateData,
    id = "iv_range_chart",
    apiPayload,
    liveData,
    instrument = "NIFTY",
    isTradingSessionActive,
    isPollingAllowed = false,
    isInstrumentLive = false,
    isLivePollingAllowed = false,
    showBottomPanel: showBottomPanelProp = true,
    bottomPanelHeightPercent: bottomPanelHeightPercentProp = IV_RANGE_BOTTOM_HEIGHT_DEFAULT,
    boundClass = ".of_chart-container-new",
    isDrawingEnabled = false,
    selectedTool = null,
    drawingObjects,
    setDrawingObjects,
    disableDrawing,
    isDrawingToolAllowed = false,
    onSelectDrawingTool,
}) => {
    const initialPayload = isFullApiPayload(apiPayload)
        ? apiPayload
        : EMPTY_API_PAYLOAD;
    const [selectedDate, setSelectedDate] = useState(getLatestDate(initialPayload));
    const [hoverIndex, setHoverIndex] = useState(null);
    const [hoverY, setHoverY] = useState(null);
    const [lineVisibility, setLineVisibility] = useState(() =>
        Object.fromEntries(
            IV_RANGE_LINE_KEYS.map((key) => [
                key,
                IV_RANGE_LINE_CONFIG[key].defaultVisible,
            ]),
        ),
    );
    const toggleLine = useCallback((key) => {
        setLineVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
    }, []);
    const accelerationRef = useRef(1);
    const [chartData, setChartData] = useState(initialPayload);
    const prevLivePollKeyRef = useRef(null);
    const interactionRef = useRef(null);
    const dragRef = useRef(null);
    const drawingLayerRef = useRef(null);
    const infoPanelRef = useRef(null);
    const crosshairRef = useRef(null);
    const lastCrosshairXRef = useRef(null);
    
    const drawingObjectDragRef = useRef(false);
    const crosshairHoverRafRef = useRef(null);
    const pendingCrosshairHoverRef = useRef(null);
    const drawingMenuRef = useRef(null);
    const drawingMenuFloatingRef = useRef(null);
    const chartShellRef = useRef(null);
    const chartPlotRef = useRef(null);
    const [isDrawingMenuOpen, setIsDrawingMenuOpen] = useState(false);
    const [drawingMenuPlacement, setDrawingMenuPlacement] = useState("toolbar");
    const [drawingMenuAnchor, setDrawingMenuAnchor] = useState({ x: 0, y: 0 });
    const [selectedDrawingObject, setSelectedDrawingObject] = useState(null);
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "Escape") setIsDrawingMenuOpen(false);
        };
        const handleClickOutside = (event) => {
            const inToolbar =
                drawingMenuRef.current?.contains(event.target) ?? false;
            const inMenu =
                drawingMenuFloatingRef.current?.contains(event.target) ?? false;
            if (!inToolbar && !inMenu) {
                setIsDrawingMenuOpen(false);
            }
        };
        if (isDrawingMenuOpen) {
            document.addEventListener("keydown", handleKeyDown);
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isDrawingMenuOpen]);

    const handleSelectDrawingTool = useCallback(
        (toolId) => {
            onSelectDrawingTool?.(toolId);
            setIsDrawingMenuOpen(false);
        },
        [onSelectDrawingTool],
    );

    const openDrawingMenuAtChart = useCallback(
        (clientX, clientY) => {
            if (!isDrawingToolAllowed) return;
            const plot = chartPlotRef.current?.getBoundingClientRect();
            if (!plot) return;
            let xCord = clientX - plot.left;
            let yCord = clientY - plot.top;
            const plotWidth = plot.width;
            if (xCord + DRAWING_CONTEXT_MENU_WIDTH > plotWidth) {
                xCord = xCord - DRAWING_CONTEXT_MENU_WIDTH + 20;
            } else {
                xCord = xCord - DRAWING_CONTEXT_MENU_WIDTH / 2;
            }
            yCord += 40;
            setDrawingMenuAnchor({ x: xCord, y: yCord });
            setDrawingMenuPlacement("chart");
            setIsDrawingMenuOpen(true);
        },
        [isDrawingToolAllowed],
    );

    const handleToggleDrawingMenu = useCallback(() => {
        if (isDrawingMenuOpen) {
            setIsDrawingMenuOpen(false);
            return;
        }
        setDrawingMenuPlacement("toolbar");
        setIsDrawingMenuOpen(true);
    }, [isDrawingMenuOpen]);

    const handleChartDoubleClick = useCallback(
        (event) => {
            event.preventDefault();
            openDrawingMenuAtChart(event.clientX, event.clientY);
        },
        [openDrawingMenuAtChart],
    );

    const chartWidth = Math.max(MIN_CHART_WIDTH, chartStateData.width || 0);
    const containerHeight = Math.max(
        MIN_CHART_HEIGHT,
        chartStateData.height || 0,
    );
    const chartHeight = Math.max(
        0,
        containerHeight -
            CONSTANTS.FIXED_HEADER_HEIGHT -
            CONSTANTS.FIXED_FOOTER_HEIGHT,
    );

    const showBottomPanel = showBottomPanelProp;
    const bottomPanelHeightPct = bottomPanelHeightPercentProp;

    useEffect(() => {
        if (!isFullApiPayload(apiPayload)) return;
        setChartData(cleanApiPayloadSessions(apiPayload));
        prevLivePollKeyRef.current = null;
        setHoverIndex(null);
        setHoverY(null);
        const latest = getLatestDate(apiPayload);
        if (latest) setSelectedDate(latest);
    }, [apiPayload]);

    useEffect(() => {
        if (!liveData) return;
        if (!isLiveTickPayload(liveData) && !isFullApiPayload(liveData)) return;
        const pollKey = livePollKey(liveData);
        if (pollKey != null && pollKey === prevLivePollKeyRef.current) return;
        prevLivePollKeyRef.current = pollKey;
        setChartData((prev) => mergeApiPayload(prev, liveData));
    }, [liveData]);

    const activePayload = isFullApiPayload(chartData)
        ? chartData
        : EMPTY_API_PAYLOAD;
    const dateList = activePayload.dateList?.length
        ? activePayload.dateList
        : getLatestDate(activePayload)
        ? [getLatestDate(activePayload)]
        : [];
    const allSessionData = useMemo(() => {
        const dateList = activePayload.dateList || [];
        const ccData = activePayload.cc_data || [];
        const combined = [];
        const liveDate = activePayload.liveDate;
        const lastTime = activePayload.lastTime;
        for (let i = 0; i < dateList.length; i += 1) {
            const sessionDate = dateList[i];
            const raw = cleanRows(
                ccData[i] || [],
                liveDate && sessionDate === liveDate ? lastTime : undefined,
            );
            const daySession = buildDaySession(raw);
            daySession.forEach((row) =>
                combined.push({ ...row, date: dateList[i] }),
            );
        }
        return combined;
    }, [activePayload]);

    const hasIv1 = useMemo(
        () => allSessionData.some((r) => Number.isFinite(r.iv1)),
        [allSessionData],
    );

    const sessionData = useMemo(() => {
        if (!selectedDate) return allSessionData;
        return allSessionData.filter((row) => row.date === selectedDate);
    }, [allSessionData, selectedDate]);

    const lastDataTime = activePayload.lastDataTime ?? null;
    const lastTime = activePayload.lastTime ?? null;
    const liveClose = useMemo(
        () =>
            resolveLiveCloseFromSession(
                sessionData,
                lastDataTime,
                lastTime,
            ),
        [sessionData, lastDataTime, lastTime],
    );
    const showLiveMarker = Boolean(
        isTradingSessionActive && lastDataTime && Number.isFinite(liveClose),
    );
    const liveBoundaryIndex = useMemo(() => {
        if (
            !isTradingSessionActive ||
            !lastDataTime ||
            selectedDate !== activePayload.liveDate
        ) {
            return -1;
        }
        const minuteKey = resolveLiveMinuteKey(lastDataTime, lastTime);
        if (minuteKey) {
            const idx = sessionData.findIndex((r) => r?.time === minuteKey);
            if (idx >= 0) return idx;
        }
        for (let i = sessionData.length - 1; i >= 0; i -= 1) {
            if (rowHasOhlc(sessionData[i])) return i;
        }
        return -1;
    }, [
        isTradingSessionActive,
        lastDataTime,
        lastTime,
        selectedDate,
        activePayload.liveDate,
        sessionData,
    ]);

    const axisLineLabels = useMemo(() => {
        if (!sessionData.length) return [];
        const out = [];
        MAIN_PANEL_LINE_KEYS.forEach((key) => {
            const config = IV_RANGE_LINE_CONFIG[key];
            if (!config || !AXIS_LABEL_FIELDS.includes(config.field)) return;
            if (!lineVisibility[key]) return;
            let row = null;
            // Last projection bar (usd/lsd, no OHLC) — e.g. 15:15, not the
            // trailing live OHLC candle the API appends after it (e.g. 11:28).
            for (let i = sessionData.length - 1; i >= 0; i -= 1) {
                const candidate = sessionData[i];
                if (isNum(candidate?.[config.field]) && !rowHasOhlc(candidate)) {
                    row = candidate;
                    break;
                }
            }
            if (!row) {
                for (let i = sessionData.length - 1; i >= 0; i -= 1) {
                    if (isNum(sessionData[i]?.[config.field])) {
                        row = sessionData[i];
                        break;
                    }
                }
            }
            if (!row) return;
            out.push({
                key,
                value: row[config.field],
                color: config.color,
                time: row.time ?? "",
            });
        });
        return out;
    }, [sessionData, lineVisibility]);

    const slotCount = useMemo(
        () => Math.max(1, sessionData.length),
        [sessionData.length],
    );

    const layout = useMemo(
        () =>
            getLayout(
                chartWidth,
                chartHeight,
                bottomPanelHeightPct / 100,
                showBottomPanel,
            ),
        [chartWidth, chartHeight, bottomPanelHeightPct, showBottomPanel],
    );

    const IV_RANGE_FAB_SIZE = 40;
    const fabPositionY = useMemo(() => {
        const chartTop = CONSTANTS.FIXED_HEADER_HEIGHT;
        if (layout.showBottomPanel) {
            return Math.max(
                chartTop,
                chartTop + layout.bottomPanelTop - IV_RANGE_FAB_SIZE - 4,
            );
        }
        return Math.max(
            chartTop,
            chartTop +
                layout.pricePlotTop +
                layout.priceInnerHeight -
                IV_RANGE_FAB_SIZE -
                8,
        );
    }, [layout]);

    // Re-anchor when day changes or when we recover from a tiny poll (broken state).
    const defaultViewKey = `${dateList.join("|")}::${selectedDate || ""}::${
        sessionData.length >= MIN_VISIBLE_CANDLES ? "full" : "partial"
    }`;

    // Default view = the WHOLE trading session (candles + projected SD band
    // running to the end of the day). Previously this was windowed to
    // lastOhlcIdx + 30 bars, which zoomed into only the plotted candles and
    // cut off the projection area.
    const defaultXView = useMemo(() => {
        const dataLength = sessionData.length;
        if (!dataLength) return { k: 1, x: 0 };
        return computeDefaultXView({
            innerWidth: layout.innerWidth,
            slotCount,
            dataLength,
            lastDayStartIndex: 0,
            dayBarCount: dataLength,
        });
    }, [layout.innerWidth, slotCount, sessionData, selectedDate]);

    const {
        xScale,
        start,
        end,
        reset: resetX,
        zoomBy: zoomX,
        panBy: panX,
    } = useXZoom({
        innerWidth: layout.innerWidth,
        slotCount,
        dataLength: sessionData.length,
        defaultXView,
        defaultViewKey,
    });

    const visibleData = useMemo(
        () => sessionData.slice(start, end),
        [sessionData, start, end],
    );

    const dayBoundaries = useMemo(() => {
        const list = [];
        for (let j = 1; j < visibleData.length; j += 1) {
            if (visibleData[j].date !== visibleData[j - 1].date)
                list.push({
                    index: start + j,
                    date: visibleData[j].date,
                });
        }
        if (start === 0 && visibleData.length)
            list.push({ index: start, date: visibleData[0].date });
        return list;
    }, [visibleData, start]);

    const {
        transform: yTransform,
        zoom: zoomY,
        panBy: panY,
        reset: resetY,
    } = useVerticalZoom();
    const {
        transform: yTransformIv,
        zoom: zoomIvY,
        panBy: panIvY,
        reset: resetIvY,
    } = useVerticalZoom();
    const priceFields = useMemo(
        () =>
            MAIN_PANEL_LINE_KEYS.filter((k) => lineVisibility[k]).map(
                (k) => IV_RANGE_LINE_CONFIG[k].field,
            ),
        [lineVisibility],
    );
    const priceAutoScale = useMemo(
        () =>
            buildPriceYScale(sessionData, layout.priceInnerHeight, priceFields),
        [sessionData, layout.priceInnerHeight, priceFields],
    );

    const priceScale = useMemo(
        () => yTransform.rescaleY(priceAutoScale),
        [yTransform, priceAutoScale],
    );

    const ivLineVisible = lineVisibility.iv !== false;
    const iv1LineVisible = hasIv1 && lineVisibility.iv1 !== false;

    const ivAutoScale = useMemo(
        () =>
            buildIvYScale(sessionData, layout.bottomInnerHeight, {
                iv: ivLineVisible,
                iv1: iv1LineVisible,
            }),
        [sessionData, layout.bottomInnerHeight, ivLineVisible, iv1LineVisible],
    );
    const ivScale = useMemo(
        () => yTransformIv.rescaleY(ivAutoScale),
        [yTransformIv, ivAutoScale],
    );

    useEffect(() => {
        resetIvY();
    }, [ivLineVisible, iv1LineVisible, resetIvY]);

    // The drawing layer owns the pointer while an object is being dragged, but
    // the chart cursor is `none` here — the painted crosshair is the only
    // pointer feedback, so it has to keep tracking through the drag.
    const handleDrawingObjectDragChange = useCallback((active) => {
        drawingObjectDragRef.current = active;
    }, []);

    const flushCrosshairHoverState = useCallback(() => {
        const pending = pendingCrosshairHoverRef.current;
        if (!pending) return;
        setHoverIndex(pending.clampedIndex);
        setHoverY(pending.hoverY);
    }, []);

    const hitInfoPanelControl = useCallback(
        (clientX, clientY) =>
            hitTestInfoPanel(infoPanelRef.current, clientX, clientY),
        [],
    );

    const updateCrosshairFromPointer = useCallback(
        (event) => {
            if (isDrawingEnabled) return;
            if (hitInfoPanelControl(event.clientX, event.clientY)?.control) {
                lastCrosshairXRef.current = null;
                crosshairRef.current?.paint({ enabled: false });
                const overlay = interactionRef.current;
                if (overlay) overlay.style.cursor = "pointer";
                return true;
            }
            const rect = interactionRef.current?.getBoundingClientRect();
            if (!rect) return;
            const chartY = event.clientY - rect.top;
            const localX = event.clientX - rect.left - CHART_MARGIN.left;
            const plotPointerY = chartY - layout.pricePlotTop;
            const index = Math.floor(xScale.invert(localX));
            const columnBottom = getVerticalMarkerColumnBottom(layout);
            const boxY = Math.max(0, Math.min(columnBottom, chartY));
            const hoverY = Math.max(
                0,
                Math.min(layout.interactiveHeight, plotPointerY),
            );
            const clampedIndex =
                index >= 0 && index < sessionData.length ? index : null;
            const chartX = event.clientX - rect.left;
            const inPlotX = localX >= 0 && localX <= layout.innerWidth;
            const pointerX = !inPlotX
                ? null
                : clampedIndex != null
                  ? CHART_MARGIN.left + xScale(clampedIndex + 0.5)
                  : CHART_MARGIN.left + localX;
            // On the price axis there is no candle to snap to — follow the pointer
            // so the vertical line stays on the axis under the cursor.
            const drawX =
                pointerX ??
                Math.max(0, Math.min(rect.width, chartX));
            lastCrosshairXRef.current = drawX;
            const inPriceBand =
                plotPointerY >= 0 && plotPointerY <= layout.priceInnerHeight;
            const priceLabel = inPriceBand
                ? formatLiveBadgePrice(priceScale.invert(plotPointerY))
                : null;
            const timeIndex = Number.isFinite(index)
                ? Math.max(0, Math.min(sessionData.length - 1, index))
                : -1;
            const timeRow =
                inPlotX && timeIndex >= 0 ? sessionData[timeIndex] : null;
            const timeLabel =
                timeRow?.date && timeRow?.time
                    ? `${timeRow.date} ${timeRow.time}`
                    : null;
            const inPlot =
                inPlotX &&
                plotPointerY >= 0 &&
                plotPointerY <= layout.interactiveHeight;
            const onPriceAxis = !inPlotX;
            const diamondX = onPriceAxis ? drawX : pointerX ?? drawX;

            crosshairRef.current?.paint({
                enabled: true,
                hoverY,
                boxY,
                pointerX,
                drawX,
                diamondX,
                showDiamond: diamondX != null,
                priceLabel,
                timeLabel,
            });

            const el = interactionRef.current;
            if (el) {
                el.style.cursor = getDrawingSurfaceCursor(isDrawingEnabled);
            }

            if (drawingObjectDragRef.current) return;
            pendingCrosshairHoverRef.current = {
                clampedIndex: inPlot ? clampedIndex : null,
                hoverY,
            };
            if (crosshairHoverRafRef.current) return;
            crosshairHoverRafRef.current = requestAnimationFrame(() => {
                crosshairHoverRafRef.current = null;
                flushCrosshairHoverState();
            });
        },
        [
            xScale,
            priceScale,
            sessionData,
            layout.innerWidth,
            layout.interactiveHeight,
            layout.priceInnerHeight,
            layout.pricePlotTop,
            layout.showBottomPanel,
            layout.bottomPanelTop,
            layout.bottomInnerHeight,
            isDrawingEnabled,
            flushCrosshairHoverState,
            hitInfoPanelControl,
        ],
    );

    useEffect(
        () => () => {
            if (crosshairHoverRafRef.current) {
                cancelAnimationFrame(crosshairHoverRafRef.current);
            }
        },
        [],
    );

    useEffect(() => {
        if (!isDrawingEnabled) return;
        crosshairRef.current?.paint({ enabled: false });
        setHoverIndex(null);
        setHoverY(null);
    }, [isDrawingEnabled]);

    const handlePointerDown = useCallback(
        (event) => {
            if (event.button !== 0) return;
            const hit = hitInfoPanelControl(event.clientX, event.clientY);
            if (hit?.control) {
                event.preventDefault();
                event.stopPropagation();
                hit.control.click();
                return;
            }
            if (isDrawingEnabled) return;
            if (hit?.inPanel) return;
            event.currentTarget.setPointerCapture?.(event.pointerId);
            const overlay = interactionRef.current ?? event.currentTarget;
            const localY =
                event.clientY -
                overlay.getBoundingClientRect().top -
                layout.pricePlotTop;
            dragRef.current = {
                pointerId: event.pointerId,
                lastX: event.clientX,
                lastY: event.clientY,
                verticalTarget: getVerticalPanTarget(localY, layout),
            };
        },
        [layout, isDrawingEnabled, hitInfoPanelControl],
    );
    const handleChartPanMove = useCallback(
        (event) => {
            if (isDrawingEnabled || drawingObjectDragRef.current) return;
            const drag = dragRef.current;
            if (!drag) return;
            const dx = event.clientX - drag.lastX;
            const dy = event.clientY - drag.lastY;
            drag.lastX = event.clientX;
            drag.lastY = event.clientY;
            if (dx !== 0) panX(dx * PAN_DRAG_SPEED);
            if (dy !== 0) {
                const sdy = dy * PAN_DRAG_SPEED;
                if (drag.verticalTarget === "bottom") panIvY(sdy);
                else if (drag.verticalTarget === "price") panY(sdy);
            }
        },
        [panX, panY, panIvY, isDrawingEnabled],
    );

    const handleInteractionPointerMove = useCallback(
        (event) => {
            updateCrosshairFromPointer(event);
            handleChartPanMove(event);
        },
        [updateCrosshairFromPointer, handleChartPanMove],
    );
    const handlePointerUp = useCallback((event) => {
        dragRef.current = null;
        if (event.currentTarget.hasPointerCapture?.(event.pointerId))
            event.currentTarget.releasePointerCapture(event.pointerId);
    }, []);
    const handleWheel = useCallback(
        (event) => {
            if (isDrawingEnabled) {
                event.preventDefault();
                return;
            }
            event.preventDefault();
            const rect =
                interactionRef.current?.getBoundingClientRect() ??
                event.currentTarget.getBoundingClientRect();
            const mouseX = event.clientX - rect.left - CHART_MARGIN.left;
            const mouseY = event.clientY - rect.top - layout.pricePlotTop;
            const rawDelta =
                event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY;
            const delta = Math.max(-100, Math.min(100, rawDelta));
            const factor = Math.exp(-delta * WHEEL_SENSITIVITY); // >1 = zoom in

            zoomX(factor, mouseX);
            const yTarget = getVerticalPanTarget(mouseY, layout);
            if (yTarget === "price") {
                zoomY(1 / factor, mouseY);
            } else if (yTarget === "bottom" && layout.bottomPlotLocalTop != null) {
                const ivAnchorY =
                    mouseY - layout.bottomPlotLocalTop - CHART_MARGIN.top;
                zoomIvY(
                    1 / factor,
                    Math.max(0, Math.min(layout.bottomInnerHeight, ivAnchorY)),
                );
            }
        },
        [zoomX, zoomY, zoomIvY, layout, isDrawingEnabled],
    );

    const xTicks = useMemo(
        () =>
            buildMultiDayTimeTicks(
                sessionData,
                xScale,
                start,
                end,
                layout.innerWidth,
            ),
        [sessionData, xScale, start, end, layout.innerWidth],
    );

    const resetView = useCallback(() => {
        resetX();
        resetY();
        resetIvY();
    }, [resetX, resetY, resetIvY]);
    const handleReset = useCallback(() => {
        crosshairRef.current?.paint({ enabled: false });
        setHoverIndex(null);
        setHoverY(null);
        resetView();
    }, [resetView]);
    const handleDateChange = useCallback((date) => {
        setSelectedDate(date);
        setHoverIndex(null);
        setHoverY(null);
    }, []);

    
    const handleOverlayEnter = useCallback(() => {
        const el = interactionRef.current;
        if (!el) return;
        el.style.outline = OUTLINE_HOVER;
        el.focus({ preventScroll: true });
    }, []);

    const handleOverlayLeave = useCallback(
        (event) => {
            const related = event?.relatedTarget;
            if (
                isChartOverlayElement(related) &&
                chartShellRef.current?.contains(related)
            ) {
                return; // moving between overlays of THIS chart only
            }
            const el = interactionRef.current;
            if (el) {
                el.style.outline = OUTLINE_DEFAULT;
                el.blur();
                el.style.cursor = getDrawingSurfaceCursor(isDrawingEnabled);
            }
            accelerationRef.current = 1;
            lastCrosshairXRef.current = null;
            crosshairRef.current?.paint({ enabled: false });
            setHoverIndex(null);
            setHoverY(null);
        },
        [isDrawingEnabled],
    );

    const handleOverlayMove = updateCrosshairFromPointer;

    const handleKeyDown = useCallback(
        (event) => {
            if (isDrawingEnabled) return;
            accelerationRef.current = Math.min(
                accelerationRef.current +
                    OF_KEYBOARD_ZOOM_PAN.ACCELERATION_STEP,
                OF_KEYBOARD_ZOOM_PAN.MAX_ACCLERATION,
            );
            const step = OF_KEYBOARD_ZOOM_PAN.PAN_STEP * accelerationRef.current * PAN_KEY_SPEED;
            if (
                event.key === KEYBOARD_SHORTCUT.RECENTER &&
                !event.metaKey &&
                !event.ctrlKey
            ) {
                event.preventDefault();
                resetView();
                return;
            }
            if (event.metaKey || event.altKey) {
                if (event.key !== "ArrowUp" && event.key !== "ArrowDown")
                    return;
                event.preventDefault();
                const magnification =
                    event.key === "ArrowUp" ? ZOOM_IN_FACTOR : ZOOM_OUT_FACTOR;
                if (event.shiftKey)
                    zoomY(magnification, layout.priceInnerHeight / 2);
                else zoomX(magnification, layout.innerWidth / 2);
                return;
            }
            switch (event.key) {
                case "ArrowLeft":
                    event.preventDefault();
                    panX(step);
                    break;
                case "ArrowRight":
                    event.preventDefault();
                    panX(-step);
                    break;
                case "ArrowUp":
                    event.preventDefault();
                    panY(step);
                    break;
                case "ArrowDown":
                    event.preventDefault();
                    panY(-step);
                    break;
                default:
                    break;
            }
        },
        [
            resetView,
            zoomX,
            zoomY,
            panX,
            panY,
            layout.priceInnerHeight,
            layout.innerWidth,
            isDrawingEnabled,
        ],
    );
    const handleKeyUp = useCallback(() => {
        accelerationRef.current = 1;
    }, []);

    const liveIndex = useMemo(() => {
        if (!lastDataTime) return -1;
        const key = String(lastDataTime).trim().split(" ").pop().slice(0, 5);
        return sessionData.findIndex((r) => r.time === key);
    }, [sessionData, lastDataTime]);

    const displayIndex =
        hoverIndex !== null && hoverIndex >= 0
            ? hoverIndex
            : liveIndex >= 0
              ? liveIndex
              : Math.max(0, sessionData.length - 1);
    const hoverRow =
        hoverIndex !== null ? sessionData[hoverIndex] ?? null : null;
    const infoRow = sessionData[displayIndex] ?? null;
    const infoDisplayTime =
        showLiveMarker && displayIndex === liveIndex && lastDataTime
            ? lastDataTime
            : null;
    useEffect(() => {
        const el = interactionRef.current;
        if (!el) return;
        el.addEventListener("wheel", handleWheel, { passive: false });
        return () => el.removeEventListener("wheel", handleWheel);
    }, [handleWheel]);
    const handleUpdateLine = useCallback((updatedLine) => {
        drawingLayerRef.current?.updateLine(updatedLine);
    }, []);
    const handleDeleteLine = useCallback(() => {
        if (!selectedDrawingObject) return;
        drawingLayerRef.current?.deleteLine(selectedDrawingObject);
    }, [selectedDrawingObject]);

    return (
        <Box
            ref={chartShellRef}
            sx={{ position: "relative", width: "100%", height: "100%" }}
        >
            {selectedDrawingObject ? (
                <Box
                    sx={{
                        position: "absolute",
                        top: CONSTANTS.FIXED_HEADER_HEIGHT + 4,
                        left: 0,
                        right: 0,
                        zIndex: 20,
                        display: "flex",
                        justifyContent: "center",
                        pointerEvents: "none",
                        "& > *": { pointerEvents: "auto" },
                    }}
                >
                    <LineToolbar
                        key={selectedDrawingObject.id}
                        id={id}
                        embedded
                        dragLine={selectedDrawingObject}
                        onUpdateLine={handleUpdateLine}
                        onDeleteLine={handleDeleteLine}
                    />
                </Box>
            ) : null}
            <IvRangeToolbar
                chartStateData={chartStateData}
                instrument={instrument}
                dateList={dateList}
                selectedDate={selectedDate || dateList[dateList.length - 1]}
                onDateChange={handleDateChange}
                isDrawingToolAllowed={isDrawingToolAllowed}
                isDrawingEnabled={isDrawingEnabled}
                selectedTool={selectedTool}
                isMenuOpen={isDrawingMenuOpen}
                drawingMenuPlacement={drawingMenuPlacement}
                onToggleDrawingMenu={handleToggleDrawingMenu}
                onSelectDrawingTool={handleSelectDrawingTool}
                menuRef={drawingMenuRef}
            />
            <LCFABModal
                recenterChart={handleReset}
                showHelp={() => {}}
                chartStateData={chartStateData}
                boundClassName={boundClass}
                bottomMargin={CONSTANTS.FIXED_FOOTER_HEIGHT}
                positionY={fabPositionY}
            />
            <div
                ref={chartPlotRef}
                style={{
                    position: "relative",
                    paddingTop: `${CONSTANTS.FIXED_HEADER_HEIGHT}px`,
                    width: "100%",
                    height: `${
                        chartStateData.height - CONSTANTS.FIXED_FOOTER_HEIGHT
                    }px`,
                    boxSizing: "border-box",
                    overflow: "visible",
                }}
            >
                {isDrawingToolAllowed &&
                isDrawingMenuOpen &&
                drawingMenuPlacement === "chart" ? (
                    <Box
                        ref={drawingMenuFloatingRef}
                        sx={{
                            position: "absolute",
                            left: 0,
                            top: 0,
                            right: 0,
                            bottom: 0,
                            pointerEvents: "none",
                            zIndex: 30,
                            overflow: "visible",
                            "& > *": { pointerEvents: "auto" },
                        }}
                    >
                        <ToolMenu
                            isOpen={isDrawingMenuOpen}
                            selectedTool={selectedTool}
                            onSelectTool={handleSelectDrawingTool}
                            top={drawingMenuAnchor.y - 80}
                            left={drawingMenuAnchor.x}
                        />
                    </Box>
                ) : null}
                <IvRangeChartBody
                    id={id}
                    width={chartStateData.width}
                    height={chartHeight}
                    layout={layout}
                    visibleData={visibleData}
                    offset={start}
                    xScale={xScale}
                    priceScale={priceScale}
                    ivScale={ivScale}
                    hasIv1={hasIv1}
                    lineVisibility={lineVisibility}
                    onToggleLine={toggleLine}
                    xTicks={xTicks}
                    dayBoundaries={dayBoundaries}
                    hoverIndex={hoverIndex}
                    hoverY={hoverY}
                    hoverRow={hoverRow}
                    infoRow={infoRow}
                    infoDisplayTime={infoDisplayTime}
                    showLiveMarker={showLiveMarker}
                    liveClose={liveClose}
                    lastDataTime={lastDataTime}
                    interactionRef={interactionRef}
                    handlePointerDown={handlePointerDown}
                    handleChartPanMove={handleChartPanMove}
                    handleInteractionPointerMove={handleInteractionPointerMove}
                    handlePointerUp={handlePointerUp}
                    handleWheel={handleWheel}
                    handleOverlayEnter={handleOverlayEnter}
                    handleOverlayLeave={handleOverlayLeave}
                    handleOverlayMove={handleOverlayMove}
                    infoPanelRef={infoPanelRef}
                    onHitInfoPanelControl={hitInfoPanelControl}
                    handleKeyDown={handleKeyDown}
                    handleKeyUp={handleKeyUp}
                    onChartDoubleClick={handleChartDoubleClick}
                    updateCrosshairFromPointer={updateCrosshairFromPointer}
                    crosshairRef={crosshairRef}
                    onDrawingObjectDragChange={handleDrawingObjectDragChange}
                    sessionData={sessionData}
                    isDrawingEnabled={isDrawingEnabled}
                    selectedTool={selectedTool}
                    drawingObjects={drawingObjects}
                    setDrawingObjects={setDrawingObjects}
                    disableDrawing={disableDrawing}
                    selectedDrawingObject={selectedDrawingObject}
                    onSelectedDrawingObjectChange={setSelectedDrawingObject}
                    drawingLayerRef={drawingLayerRef}
                    instrument={instrument}
                    liveBoundaryIndex={liveBoundaryIndex}
                    axisLineLabels={axisLineLabels}
                />
            </div>
        </Box>
    );
};

export default IvRangeChart;
export * from "./ivRangeConstants";